import React, { useState, useEffect, useRef } from "react";
import { Input } from "./input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { cn } from "../../lib/utils";

// Country data with flags and dial codes
const countries = [
  { code: "CA", name: "Canada", dialCode: "+1", flag: "🇨🇦" },
  { code: "US", name: "United States", dialCode: "+1", flag: "🇺🇸" },
  { code: "GB", name: "United Kingdom", dialCode: "+44", flag: "🇬🇧" },
  { code: "AU", name: "Australia", dialCode: "+61", flag: "🇦🇺" },
  { code: "IN", name: "India", dialCode: "+91", flag: "🇮🇳" },
  { code: "MX", name: "Mexico", dialCode: "+52", flag: "🇲🇽" },
  { code: "FR", name: "France", dialCode: "+33", flag: "🇫🇷" },
  { code: "DE", name: "Germany", dialCode: "+49", flag: "🇩🇪" },
  { code: "IT", name: "Italy", dialCode: "+39", flag: "🇮🇹" },
  { code: "ES", name: "Spain", dialCode: "+34", flag: "🇪🇸" },
  { code: "BR", name: "Brazil", dialCode: "+55", flag: "🇧🇷" },
  { code: "JP", name: "Japan", dialCode: "+81", flag: "🇯🇵" },
  { code: "CN", name: "China", dialCode: "+86", flag: "🇨🇳" },
  { code: "KR", name: "South Korea", dialCode: "+82", flag: "🇰🇷" },
  { code: "NZ", name: "New Zealand", dialCode: "+64", flag: "🇳🇿" },
];

const DEFAULT_COUNTRY = "CA"; // Canada

// Detect country from phone number
const detectCountryFromPhone = (phoneNumber) => {
  if (!phoneNumber || phoneNumber.trim().length === 0) {
    return DEFAULT_COUNTRY;
  }

  // Remove all non-numeric characters except +
  const cleaned = phoneNumber.replace(/[^\d+]/g, "");

  // Check each country's dial code
  for (const country of countries) {
    if (cleaned.startsWith(country.dialCode)) {
      // For +1, check if it's Canada or US based on area code
      if (country.dialCode === "+1") {
        // Canadian area codes
        const canadianAreaCodes = [
          "403",
          "587",
          "780",
          "825", // Alberta
          "236",
          "250",
          "604",
          "672",
          "778", // British Columbia
          "204",
          "431", // Manitoba
          "506", // New Brunswick
          "709", // Newfoundland and Labrador
          "782",
          "902", // Nova Scotia
          "226",
          "249",
          "289",
          "343",
          "365",
          "416",
          "437",
          "519",
          "548",
          "613",
          "647",
          "705",
          "807",
          "905", // Ontario
          "418",
          "438",
          "450",
          "514",
          "579",
          "581",
          "819",
          "873", // Quebec
          "306",
          "639", // Saskatchewan
          "867", // Yukon, Northwest Territories, Nunavut
        ];
        const areaCode = cleaned.substring(2, 5);
        if (canadianAreaCodes.includes(areaCode)) {
          return "CA";
        }
        return "US";
      }
      return country.code;
    }
  }

  return DEFAULT_COUNTRY;
};

// Format phone number with country code
const formatPhoneNumber = (value, dialCode) => {
  // Remove all non-numeric characters
  const digitsOnly = value.replace(/\D/g, "");

  // If value already starts with dial code, remove it
  const dialCodeDigits = dialCode.replace(/\D/g, "");
  let phoneDigits = digitsOnly;
  if (digitsOnly.startsWith(dialCodeDigits)) {
    phoneDigits = digitsOnly.substring(dialCodeDigits.length);
  }

  // Format based on country
  if (dialCode === "+1") {
    // North American format: (XXX) XXX-XXXX
    if (phoneDigits.length <= 3) {
      return phoneDigits;
    } else if (phoneDigits.length <= 6) {
      return `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3)}`;
    } else {
      return `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(
        3,
        6,
      )}-${phoneDigits.slice(6, 10)}`;
    }
  } else {
    // For other countries, just add spaces every 3-4 digits
    return phoneDigits.match(/.{1,4}/g)?.join(" ") || phoneDigits;
  }
};

// Validate phone number
const validatePhoneNumber = (value, dialCode) => {
  if (!value || value.trim().length === 0) {
    return { isValid: false, error: "Phone number is required" };
  }

  const digitsOnly = value.replace(/\D/g, "");
  const dialCodeDigits = dialCode.replace(/\D/g, "");

  // Remove country code from digits if present
  let phoneDigits = digitsOnly;
  if (digitsOnly.startsWith(dialCodeDigits)) {
    phoneDigits = digitsOnly.substring(dialCodeDigits.length);
  }

  // Validation rules
  if (dialCode === "+1") {
    // North American: 10 digits
    if (phoneDigits.length !== 10) {
      return {
        isValid: false,
        error: "Please enter a valid 10-digit phone number",
      };
    }
    // Check if area code is valid (first digit should be 2-9)
    if (phoneDigits[0] < "2" || phoneDigits[0] > "9") {
      return { isValid: false, error: "Area code must start with 2-9" };
    }
    // Check if exchange code is valid (4th digit should be 2-9)
    if (phoneDigits[3] < "2" || phoneDigits[3] > "9") {
      return { isValid: false, error: "Exchange code must start with 2-9" };
    }
  } else {
    // For other countries, check minimum length (usually 7-15 digits)
    if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      return { isValid: false, error: "Please enter a valid phone number" };
    }
  }

  return { isValid: true, error: null };
};

export const PhoneInput = React.forwardRef(
  (
    {
      value = "",
      onChange,
      onBlur,
      className = "",
      defaultCountry = DEFAULT_COUNTRY,
      error,
      ...props
    },
    ref,
  ) => {
    const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
    const [phoneValue, setPhoneValue] = useState("");
    const [validationError, setValidationError] = useState(null);
    const inputRef = useRef(null);

    // Get country data
    const country =
      countries.find((c) => c.code === selectedCountry) || countries[0];

    // Initialize phone value from prop
    useEffect(() => {
      if (value) {
        // Detect country from existing value
        const detectedCountry = detectCountryFromPhone(value);
        const detectedCountryData =
          countries.find((c) => c.code === detectedCountry) || countries[0];
        setSelectedCountry(detectedCountry);

        // Extract phone number without country code
        const cleaned = value.replace(/\D/g, "");
        const dialCodeDigits = detectedCountryData.dialCode.replace(/\D/g, "");
        let phoneDigits = cleaned;
        if (cleaned.startsWith(dialCodeDigits)) {
          phoneDigits = cleaned.substring(dialCodeDigits.length);
        }

        // Format the phone number
        const formatted = formatPhoneNumber(
          phoneDigits,
          detectedCountryData.dialCode,
        );
        setPhoneValue(formatted);
      } else {
        setPhoneValue("");
      }
    }, [value]);

    // Handle country change
    const handleCountryChange = (countryCode) => {
      setSelectedCountry(countryCode);
      setValidationError(null);

      // Re-format phone number with new country code
      if (phoneValue) {
        const newCountry = countries.find((c) => c.code === countryCode);
        const formatted = formatPhoneNumber(phoneValue, newCountry.dialCode);
        setPhoneValue(formatted);

        // Trigger onChange with full number
        const fullNumber = `${newCountry.dialCode}${formatted.replace(
          /\D/g,
          "",
        )}`;
        if (onChange) {
          const syntheticEvent = {
            target: { value: fullNumber, name: props.name },
          };
          onChange(syntheticEvent);
        }
      }
    };

    // Handle phone input change
    const handlePhoneChange = (e) => {
      const input = e.target.value;

      // Remove formatting characters but keep digits
      const digitsOnly = input.replace(/\D/g, "");

      // Limit length based on country
      const maxLength = country.dialCode === "+1" ? 10 : 15;
      if (digitsOnly.length > maxLength) {
        return;
      }

      // Format the phone number
      const formatted = formatPhoneNumber(digitsOnly, country.dialCode);
      setPhoneValue(formatted);

      // Combine country code and phone number
      const fullNumber = `${country.dialCode}${digitsOnly}`;

      // Validate
      const validation = validatePhoneNumber(fullNumber, country.dialCode);
      setValidationError(validation.error);

      // Trigger onChange
      if (onChange) {
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: fullNumber,
            name: props.name,
          },
        };
        onChange(syntheticEvent);
      }
    };

    // Handle blur
    const handleBlur = (e) => {
      const fullNumber = `${country.dialCode}${phoneValue.replace(/\D/g, "")}`;
      const validation = validatePhoneNumber(fullNumber, country.dialCode);
      setValidationError(validation.error);

      if (onBlur) {
        onBlur(e);
      }
    };

    // Combine refs
    React.useImperativeHandle(ref, () => inputRef.current);

    const displayError = error || validationError;

    return (
      <div
        className={cn("w-full", className.includes("rounded") && "rounded-xl")}>
        <div className="flex gap-2">
          {/* Country Selector */}
          <Select value={selectedCountry} onValueChange={handleCountryChange}>
            <SelectTrigger className="w-[140px] h-12 shrink-0 rounded-xl border-2 border-gray-300 focus:border-emerald-500">
              <SelectValue>
                <div className="flex items-center gap-2">
                  <span className="text-xl">{country.flag}</span>
                  <span className="text-sm">{country.dialCode}</span>
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {countries.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  <div className="flex items-center gap-2">
                    <span>{c.flag}</span>
                    <span>{c.name}</span>
                    <span className="text-muted-foreground ml-auto">
                      {c.dialCode}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Phone Input */}
          <div className="flex-1 relative">
            <Input
              ref={inputRef}
              type="tel"
              value={phoneValue}
              onChange={handlePhoneChange}
              onBlur={handleBlur}
              placeholder={
                country.dialCode === "+1"
                  ? "(555) 123-4567"
                  : "Enter phone number"
              }
              className={cn(
                "h-12 w-full",
                displayError && "border-red-500 focus-visible:ring-red-500",
                className,
              )}
              {...props}
            />
          </div>
        </div>

        {/* Error Message */}
        {displayError && (
          <p className="text-sm text-red-500 mt-1">{displayError}</p>
        )}
      </div>
    );
  },
);

PhoneInput.displayName = "PhoneInput";

export default PhoneInput;
